/**
 * How long a request may take before axios gives up on it (#214).
 *
 * axios ships with `timeout: 0`, which means "wait forever", and nothing in this
 * app ever changed it. An ordinary network failure still errors — the socket
 * closes and the browser says so — but a HALF-OPEN connection emits nothing at
 * all: the phone moves from Wi-Fi to cellular, or a carrier NAT drops the flow,
 * and the request's promise simply never settles. No error, no snackbar, no
 * interceptor, a spinner that spins until the screen is closed.
 *
 * It also left `USER_MESSAGES.timeout` in apiErrorUtils as dead copy. That
 * branch is keyed on `error.code === "ECONNABORTED"`, which axios only ever
 * emits when a deadline is configured — so the message was reachable by
 * ORDERING and unreachable IN FACT.
 *
 * Numbers only here, and no imports at all. The assignment that installs the
 * default lives in useAxios, which already owns axios's global defaults for the
 * app. Keeping the side effect out of this file matters: the upload exemption
 * below is imported by three API modules, and if importing a constant also
 * mutated the axios singleton then every suite that reaches one of those
 * modules through a minimal `jest.mock("axios")` would fail at import time —
 * which is exactly what happened when it did.
 */

/**
 * 90 s, and the floor is what sets it, not the ceiling.
 *
 * The server is on Render's free tier: it spins down after 15 minutes of
 * inactivity and the next caller pays a cold start, which Render documents as up
 * to a minute or more, on top of this server's own mongoose connect. On a
 * low-traffic app that caller is a large share of all callers, so a 30 s
 * deadline would tell users "Request timed out" about a request that was, in
 * fact, on its way — trading a rare hang for a routine false failure. Budget
 * ~60 s for the cold start and ~30 s for a slow mobile round trip after it.
 *
 * The ceiling barely matters, because the failure being fixed emits nothing
 * ever: any finite number is the whole of the fix. 90 s is chosen to sit clear
 * of the slowest honest request, not to feel snappy.
 */
export const DEFAULT_TIMEOUT_MS = 90_000;

/**
 * 10 minutes, for the photo uploads only.
 *
 * server/upload/uploadRoutes.js accepts MAX_FILES (5) × MAX_FILE_BYTES (5 MB) =
 * 25 MB in one request, and answers only after re-encoding every image and
 * running each through AI moderation. 25 MB over a poor-but-usable 1 Mbps uplink
 * is ~200 s of transfer before any of that work begins, so the default above
 * would abort uploads that were going to succeed — and killing a nearly complete
 * 25 MB upload is a worse bug than the hang this file exists to fix.
 *
 * Still finite, deliberately. The exemption is a LARGER deadline, not the
 * absence of one; a stalled upload has to end too, or the original bug simply
 * moves to the slowest requests in the app.
 */
export const UPLOAD_TIMEOUT_MS = 600_000;
